// src/util/freshPickValidator.js

import { fetchProviderTransactionsCount } from './api';
import { types as sdkTypes } from 'sharetribe-flex-sdk';
const { UUID } = sdkTypes;

const PLAN_LIMITS = {
  FREE: 5,
  GROWER: 50,
  PLUS: null,
};

export const freshPickValidator = async ({ sdk, providerId }) => {
  if (!sdk || !providerId) {
    throw new Error('SDK and providerId are required');
  }

  let user;

  try {
    const userRes = await sdk.users.show({
      id: new UUID(providerId),
    });

    user = userRes.data.data;
  } catch (err) {
    console.error('Failed to fetch user', err);
    throw err;
  }

  const publicData = user?.attributes?.profile?.publicData || {};
  const activatedPlan = (publicData?.activatedPlan || 'FREE').toUpperCase();
  const { billingCycleStart, billingCycleEnd } = publicData;

  const toDate = ts =>
    ts
      ? ts.toString().length === 10
        ? new Date(ts * 1000)
        : new Date(ts)
      : null;

  let periodStart;
  let periodEnd;

  const createdAt = user?.attributes?.createdAt;

  if (billingCycleStart && billingCycleEnd) {
    periodStart = toDate(billingCycleStart);
    periodEnd = toDate(billingCycleEnd);
  } else if (createdAt) {
    const createdDate = new Date(createdAt);
    const now = new Date();

    // Calculate how many months have passed since signup
    const monthsDiff =
      (now.getFullYear() - createdDate.getFullYear()) * 12 +
      (now.getMonth() - createdDate.getMonth());

    // Current cycle start = createdAt + monthsDiff months
    periodStart = new Date(createdDate);
    periodStart.setMonth(periodStart.getMonth() + monthsDiff);

    // Current cycle end = start + 1 month
    periodEnd = new Date(periodStart);
    periodEnd.setMonth(periodEnd.getMonth() + 1);

    try {
      await sdk.currentUser.updateProfile({
        publicData: {
          ...publicData,
          billingCycleStart: Math.floor(periodStart.getTime() / 1000),
          billingCycleEnd: Math.floor(periodEnd.getTime() / 1000),
        },
      });
    } catch (err) {
      console.error('Failed to persist billing cycle', err);
    }
  } else {
    // Absolute fallback (should never happen)
    periodEnd = new Date();
    periodStart = new Date();
    periodStart.setDate(periodStart.getDate() - 30);
  }

  let freshPicksUsed = 0;

  try {
    const response = await fetchProviderTransactionsCount({
      providerId,
      periodStart,
      periodEnd
    });

    freshPicksUsed = response.transactionsCount || 0;
  } catch (err) {
    console.error('FreshPick query failed', err);
  }

  const limit = PLAN_LIMITS.hasOwnProperty(activatedPlan)
    ? PLAN_LIMITS[activatedPlan]
    : PLAN_LIMITS.FREE;

  const limitReached = limit !== null && freshPicksUsed >= limit;
  const remaining = limit === null ? null : Math.max(limit - freshPicksUsed, 0);

  return {
    plan: activatedPlan,
    freshPicksUsed,
    limit,
    remaining,
    limitReached,
    billingCycleStart: periodStart,
    billingCycleEnd: periodEnd,
    resetDate: periodEnd,
  };
};
